import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUserCheck, FiSmartphone, FiTrendingUp, FiAward, FiClock, FiFilter, FiChevronDown, FiExternalLink } from 'react-icons/fi';
import { RiMoneyRupeeCircleFill } from 'react-icons/ri';
import { BsStars, BsGraphUpArrow } from 'react-icons/bs';
import Navbar from './/Navbar';
import './OffersPage.css';

const AffiliateOffers = () => {
  const [offers, setOffers] = useState([]);
  const [globalSettings, setGlobalSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('payout');
  const [expandedOffer, setExpandedOffer] = useState(null);
  const [highestPayout, setHighestPayout] = useState('₹0');
  const [topConversionRate, setTopConversionRate] = useState('0%');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch global settings
        const settingsResponse = await axios.post('https://offers.cashbolt.in.net/server/', {
          action: 'getGlobalSettings'
        });

        if (settingsResponse.data.status) {
          setGlobalSettings(settingsResponse.data.data);
        }

        // Fetch offers
        const offersResponse = await axios.post('https://offers.cashbolt.in.net/server/', {
          action: 'getOffers'
        });

        if (offersResponse.data.status) {
          // Transform the API response to match our component structure
          const formattedOffers = offersResponse.data.data.map(offer => ({
            id: offer.id,
            name: offer.name,
            type: offer.type || 'registration',
            logo: offer.logo || `https://via.placeholder.com/80/4F46E5/FFFFFF?text=${offer.name.charAt(0)}`,
            description: offer.description || 'Get paid for conversions through your affiliate link',
            payout: `₹${offer.payout || '0'}`,
            requirements: offer.requirements || 'Complete specified actions',
            conversionRate: offer.conversionRate || '0.0',
            expiry: offer.expiry || '2023-12-31',
            featured: offer.featured || false,
            colors: offer.colors || ['#4F46E5', '#7C3AED'],
            // Add offer URL if available from API, otherwise use a placeholder
            offerUrl: offer.url || `https://offers.cashbolt.in.net/offer/${offer.id}`
          }));
          
          setOffers(formattedOffers);
          
          // Calculate highest payout
          const maxPayout = Math.max(...formattedOffers.map(offer => 
            parseFloat(offer.payout.replace(/[^0-9.]/g, ''))
          ));
          setHighestPayout(`₹${maxPayout}`);
          
          // Calculate top conversion rate
          const maxConversion = Math.max(...formattedOffers.map(offer => 
            parseFloat(offer.conversionRate)
          ));
          setTopConversionRate(`${maxConversion}%`);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        // Fallback to mock data if API fails
        const mockOffers = [
          {
            id: 1,
            name: 'WealthApp Signup',
            type: 'registration',
            logo: 'https://via.placeholder.com/80/4F46E5/FFFFFF?text=WA',
            description: 'Get paid for every user who registers through your link and completes KYC verification',
            payout: '₹850',
            requirements: 'Complete registration + KYC',
            conversionRate: '8.2',
            expiry: '2023-12-31',
            featured: true,
            colors: ['#4F46E5', '#7C3AED'],
            offerUrl: 'https://offers.cashbolt.in.net/offer/1'
          },
          {
            id: 2,
            name: 'TradePro App Install',
            type: 'app-install',
            logo: 'https://via.placeholder.com/80/059669/FFFFFF?text=TP',
            description: 'Earn commission for every app install that completes onboarding',
            payout: '₹550',
            requirements: 'App install + basic onboarding',
            conversionRate: '15.1',
            expiry: '2023-11-15',
            featured: false,
            colors: ['#059669', '#10B981'],
            offerUrl: 'https://offers.cashbolt.in.net/offer/2'
          }
        ];
        
        setOffers(mockOffers);
        
        // Calculate highest payout for mock data
        const maxPayout = Math.max(...mockOffers.map(offer => 
          parseFloat(offer.payout.replace(/[^0-9.]/g, ''))
        ));
        setHighestPayout(`₹${maxPayout}`);
        
        // Calculate top conversion rate for mock data
        const maxConversion = Math.max(...mockOffers.map(offer => 
          parseFloat(offer.conversionRate)
        ));
        setTopConversionRate(`${maxConversion}%`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredOffers = activeFilter === 'all' 
    ? offers 
    : offers.filter(offer => offer.type === activeFilter);

  const sortedOffers = [...filteredOffers].sort((a, b) => {
    if (sortBy === 'payout') {
      return parseFloat(b.payout.replace(/[^0-9.]/g, '')) - parseFloat(a.payout.replace(/[^0-9.]/g, ''));
    } else if (sortBy === 'conversion') {
      return parseFloat(b.conversionRate) - parseFloat(a.conversionRate);
    } else {
      return new Date(a.expiry) - new Date(b.expiry);
    }
  });

  const filters = [
    { id: 'all', name: 'All Offers', icon: <FiTrendingUp /> },
    { id: 'registration', name: 'Registrations', icon: <FiUserCheck /> },
    { id: 'app-install', name: 'App Installs', icon: <FiSmartphone /> },
    { id: 'kyc', name: 'KYC Completions', icon: <FiUserCheck /> },
    { id: 'deposit', name: 'First Deposits', icon: <RiMoneyRupeeCircleFill /> },
    { id: 'referral', name: 'Referrals', icon: <FiAward /> }
  ];

  const handleCompleteOffer = (offerUrl) => {
    // Redirect to the offer page
    window.open(offerUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <motion.div
          className="spinner-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            className="spinner"
          >
            <div className="spinner-inner"></div>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Loading money-making opportunities...
          </motion.p>
          <motion.p
            className="loading-subtext"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            transition={{ delay: 0.6 }}
          >
            Preparing high-converting offers for you
          </motion.p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="affiliate-offers">
      <Navbar siteName={globalSettings.name || 'Affiliate Offers'} />
      {/* Hero Section */}
      <motion.section 
        className="hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-pattern"></div>
        <div className="hero-content">
          <motion.div
            className="hero-text"
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <motion.div 
              className="badge"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
            >
              <BsStars /> Premium Offers
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Maximize Your <span>Affiliate Earnings</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.8 }}
              transition={{ delay: 0.5 }}
            >
              Partner with India's highest converting campaigns and boost your income
            </motion.p>
          </motion.div>
          
          <div className="hero-stats">
            <motion.div 
              className="stat-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              whileHover={{ y: -5 }}
            >
              <div className="stat-icon">
                <RiMoneyRupeeCircleFill />
              </div>
              <div>
                <h3>Highest Payout</h3>
                <p>{highestPayout} per conversion</p>
              </div>
            </motion.div>
            
            <motion.div 
              className="stat-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              whileHover={{ y: -5 }}
            >
              <div className="stat-icon">
                <BsGraphUpArrow />
              </div>
              <div>
                <h3>Top Conversion</h3>
                <p>{topConversionRate} success rate</p>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Filters and Sorting */}
      <motion.div 
        className="controls"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <div className="filter-scroll-container">
          <div className="filter-buttons">
            {filters.map(filter => (
              <motion.button
                key={filter.id}
                className={`filter-btn ${activeFilter === filter.id ? 'active' : ''}`}
                onClick={() => setActiveFilter(filter.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + (filter.id * 0.1) }}
              >
                <span className="filter-icon">{filter.icon}</span>
                {filter.name}
                {activeFilter === filter.id && (
                  <motion.span 
                    className="active-indicator"
                    layoutId="activeFilter"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </motion.button>
            ))}
          </div>
        </div>
        
        <motion.div 
          className="sort-dropdown"
          whileHover={{ scale: 1.02 }}
        >
          <FiFilter className="filter-icon" />
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="payout">Highest Payout</option>
            <option value="conversion">Best Conversion</option>
            <option value="expiry">Expiring Soon</option>
          </select>
          <FiChevronDown className="chevron-icon" />
        </motion.div>
      </motion.div>

      {/* Offers Grid */}
      <div className="offers-container">
        <AnimatePresence>
          {sortedOffers.length > 0 ? (
            sortedOffers.map((offer) => (
              <motion.div
                key={offer.id}
                className={`offer-card ${offer.featured ? 'featured' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                layout
                whileHover={{ y: -5 }}
                style={{
                  '--gradient-start': offer.colors[0],
                  '--gradient-end': offer.colors[1]
                }}
              >
                {offer.featured && (
                  <motion.div
                    className="featured-badge"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <FiAward /> Featured
                  </motion.div>
                )}
                
                <div className="offer-header">
                  <motion.div 
                    className="logo-container"
                    whileHover={{ rotate: 5, scale: 1.05 }}
                  >
                    <img src={offer.logo} alt={offer.name} />
                  </motion.div>
                  <div className="offer-title">
                    <h3>{offer.name}</h3>
                    <span className="offer-type">{offer.type.replace('-', ' ')}</span>
                  </div>
                </div>
                
                <p className="offer-description">{offer.description}</p>
                
                <div className="offer-stats">
                  <div className="stat">
                    <div className="stat-icon">
                      <RiMoneyRupeeCircleFill />
                    </div>
                    <div>
                      <span>Payout</span>
                      <strong>{offer.payout}</strong>
                    </div>
                  </div>
                  <div className="stat">
                    <div className="stat-icon">
                      <FiTrendingUp />
                    </div>
                    <div>
                      <span>Conversion</span>
                      <strong>{offer.conversionRate}%</strong>
                    </div>
                  </div>
                </div>
                
                <motion.button 
                  className="cta-button"
                  onClick={() => handleCompleteOffer(offer.offerUrl)}
                  whileHover={{ scale: 1.02, boxShadow: `0 5px 15px ${offer.colors[0]}40` }}
                  whileTap={{ scale: 0.98 }}
                >
                  Complete Offer <FiExternalLink />
                </motion.button>
                
                <AnimatePresence>
                  {expandedOffer === offer.id && (
                    <motion.div
                      className="offer-details"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="detail-row">
                        <span>Requirements:</span>
                        <strong>{offer.requirements}</strong>
                      </div>
                      <div className="detail-row">
                        <span>Expires:</span>
                        <strong>{new Date(offer.expiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                      </div>
                      <div className="detail-row">
                        <span>Tracking:</span>
                        <strong>Real-time analytics</strong>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))
          ) : (
            <motion.div
              className="no-offers"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <h3>No offers found for this filter</h3>
              <p>Try selecting a different category or check back later</p>
              <button 
                className="reset-filter"
                onClick={() => setActiveFilter('all')}
              >
                Show All Offers
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* CTA Section */}
      <motion.section 
        className="cta-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="cta-pattern"></div>
        <div className="cta-content">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Ready to maximize your earnings?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 0.8, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Join our premium affiliate program for exclusive high-value offers
          </motion.p>
          <div className="cta-buttons">
            <motion.button
              className="primary-cta"
              whileHover={{ y: -3, boxShadow: '0 10px 20px rgba(79, 70, 229, 0.3)' }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              Join Affiliate
            </motion.button>
            <motion.button
              className="secondary-cta"
              whileHover={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
            >
              Contact Sales
            </motion.button>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default AffiliateOffers;